/**
 * useOmnisearch — composable de recherche OmniSearch
 *
 * - Debounce 500 ms
 * - AbortController pour annuler les requêtes en vol
 * - Minimum 3 caractères (aligné sur les index trigram pg_trgm côté back :
 *   en dessous de 3 chars, Postgres ne peut pas utiliser les index)
 * - Limite overlay : 5 / page complète : 20
 */
import { watch, onUnmounted } from 'vue'
import { useOmnisearchStore } from '@/stores/useOmnisearchStore'
import { searchAll } from '@/api/omnisearchApi'

const DEBOUNCE_MS = 500
const OVERLAY_LIMIT = 5
const PAGE_LIMIT = 20

export const MIN_QUERY_LENGTH = 3

export function isQueryLongEnough(q: string): boolean {
  return q.trim().length >= MIN_QUERY_LENGTH
}

export function useOmnisearch(mode: 'overlay' | 'page' = 'overlay') {
  const store = useOmnisearchStore()

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let abortController: AbortController | null = null

  function cancelPending() {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    if (abortController) {
      abortController.abort()
      abortController = null
    }
  }

  async function runSearch(q: string) {
    cancelPending()

    if (!isQueryLongEnough(q)) {
      store.setResults({ quotes: [], orders: [], invoices: [], services: [], companies: [], contacts: [], leads: [], tickets: [], campaigns: [], eligibilities: [], opportunities: [] })
      store.setLoading(false)
      return
    }

    abortController = new AbortController()
    store.setLoading(true)

    try {
      const limit = mode === 'overlay' ? OVERLAY_LIMIT : PAGE_LIMIT
      const results = await searchAll(q.trim(), limit, abortController.signal)
      store.setResults(results)
      store.setLoading(false)
    } catch (err: unknown) {
      // AbortError = requête annulée volontairement → pas d'erreur
      if (err instanceof Error && err.name === 'AbortError') return
      store.setError(true)
    }
  }

  function scheduleSearch(q: string) {
    cancelPending()

    if (!isQueryLongEnough(q)) {
      store.setLoading(false)
      store.setResults({ quotes: [], orders: [], invoices: [], services: [], companies: [], contacts: [], leads: [], tickets: [], campaigns: [], eligibilities: [], opportunities: [] })
      return
    }

    store.setLoading(true)
    debounceTimer = setTimeout(() => runSearch(q), DEBOUNCE_MS)
  }

  // Watch automatique sur le query du store
  const stopWatcher = watch(
    () => store.query,
    (q) => scheduleSearch(q),
    { immediate: false },
  )

  onUnmounted(() => {
    stopWatcher()
    cancelPending()
  })

  return {
    scheduleSearch,
    cancelPending,
  }
}
